import { useEffect, useMemo, useState } from "react";
import { Box, Container, useMediaQuery } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Loader } from "swyft-react-core";

import { Header, Benefit, CheckoutBreadcrumb, CheckoutTabs, Summary, TimerSelectDate } from "./components";
import { CheckoutStepType, checkoutSteps } from "./checkoutPage.constants";
import { useStyles } from "./checkoutPage.styles";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { RootState, history } from "../../app/rootReducer";
import { closeSummary, updateDateSelected, updateTabSelected } from "../../store/checkoutPage.slide";
import { SummaryDialog } from "./components/Summary/SummaryDialog";
import { EEventType, eventAnalytic } from "../../app/analytics";
import { fetchProductSubscriptions } from "../../store/ProductSubscriptionSlice";
import { fetchQuote, fetchSolarReport, releaseQuote, syncQuote, viewedCheckout } from "../../store/QuoteSlice";
import { useQuoteEventSlots } from "../../api/EventSlot/EventSlotApi";
import { useConfig } from "../../components/config/ConfigContext";
import { EventSlotQueryParams } from "../../api/EventSlot/EventSlot";
import { FeatureSchedule } from "../../app/consts";
import { hasAddons, quoteAvailableSubscriptions } from "../../app/utils";
import { SaleBanner } from "../../components/SaleBanner";
import { EQuoteStatus } from "../../api/Quote/EQuoteStatus";

type Props = RouteComponentProps<{ id: string }>;
export const CheckoutPage = ({ match }: Props) => {
  eventAnalytic.useEffect(EEventType.PageCheckout);
  const classes = useStyles();
  const { isMoreThanSm, isMoreThanMd } = useBreakpoint();
  const matchesSmallMobile = useMediaQuery("(max-width:360px)");
  const dispatch = useDispatch();
  const { date, timer } = useSelector((state: RootState) => state.checkout);
  const { isFeatureEnabled } = useConfig();
  const quoteId = match.params.id;
  const [eventSlotQuote] = useState<EventSlotQueryParams>({
    quoteId,
    page: 1,
    pageSize: 1000
  });
  const [rendered, setRendered] = useState<boolean>(false);
  const { quote } = useSelector((state: RootState) => state.quote);
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);

  const { data: eventSlots } = useQuoteEventSlots(eventSlotQuote, isFeatureEnabled(FeatureSchedule));
  const isAvailableSchedule = useMemo(() => {
    return (eventSlots!?.items?.length ?? 0) > 0;
  }, [eventSlots]);

  const isAvailableAddon = useMemo(() => {
    if (subscriptions && quote)
      return hasAddons(quote.addons) || !!quoteAvailableSubscriptions(quote, subscriptions).length;
    return false;
  }, [subscriptions, quote]);

  const currentCheckoutSteps = useMemo(() => {
    const steps = checkoutSteps.map((item) => {
      if (item.id === "installation-date" && !isAvailableSchedule) return false;
      if (item.id === "add-ons" && !isAvailableAddon) return false;
      return item;
    });
    return steps.filter(Boolean) as CheckoutStepType[];
  }, [isAvailableSchedule, isAvailableAddon]);

  useEffect(() => {
    dispatch(fetchProductSubscriptions());
    dispatch(fetchQuote(quoteId));
    dispatch(syncQuote(quoteId, true));
    dispatch(viewedCheckout(quoteId));
  }, [quoteId, dispatch]);

  const isSolar = quote?.type?.includes("Solar");

  useEffect(() => {
    if (isSolar) {
      dispatch(fetchSolarReport({ quoteId }));
    }
  }, [dispatch, isSolar, quoteId]);

  useEffect(() => {
    if (eventSlots) setRendered(true);
  }, [eventSlots]);

  useEffect(() => {
    dispatch(updateTabSelected(currentCheckoutSteps[0]?.id));
  }, [currentCheckoutSteps, dispatch]);

  useEffect(() => {
    if (!quote) return;
    if (quote?.status !== EQuoteStatus.Sent) {
      history.goBack();
    }
  }, [quote]);

  return (
    <Box bgcolor={"#f5fafd"} className={classes.root} position={"relative"}>
      <SaleBanner quoteId={quoteId} />
      <Box
        bgcolor={isMoreThanSm ? "transparent" : "#fff"}
        boxShadow={isMoreThanSm ? "none" : "0 2px 4px 0 rgba(152, 152, 152, 0.5)"}
        mb={isMoreThanSm ? 0 : matchesSmallMobile ? "16px" : "26px"}
      >
        <Container maxWidth={"lg"} style={matchesSmallMobile ? { padding: 0 } : {}}>
          <Header rendered={rendered} />
          {isMoreThanSm && rendered && <Benefit />}
          {rendered && <CheckoutBreadcrumb checkoutSteps={currentCheckoutSteps} />}
        </Container>
      </Box>
      {rendered ? (
        <Container maxWidth="lg" style={matchesSmallMobile ? { padding: 0 } : {}}>
          <Box display={"flex"} flexWrap={isMoreThanMd ? "nowrap" : "wrap"} justifyContent={"space-between"} pb="75px">
            <Box flexBasis={isMoreThanMd ? "64%" : "100%"}>
              <CheckoutTabs checkoutSteps={currentCheckoutSteps} quoteId={quoteId} />
            </Box>
            {isMoreThanMd && (
              <Box flexBasis={isMoreThanMd ? "34%" : "100%"}>
                <Summary quoteId={quoteId} />
              </Box>
            )}
          </Box>
        </Container>
      ) : (
        <Container>
          <Box minHeight={"50vh"} display="flex" alignItems={"center"} justifyContent={"center"}>
            <Loader />
          </Box>
        </Container>
      )}
      {date && timer && (
        <TimerSelectDate
          date={new Date(timer)}
          onClick={() => {
            dispatch(releaseQuote(quoteId));
            dispatch(updateDateSelected(undefined));
            dispatch(updateTabSelected("installation-date"));
            if (!isMoreThanMd) dispatch(closeSummary());
          }}
        />
      )}
      <SummaryDialog quoteId={quoteId} />
    </Box>
  );
};
